// Dev-session auth backend (no Cognito pool). /dev-login?as=doctor issues an httpOnly cookie; the API
// treats the dev session as MFA + step-up satisfied so the Healthie hand-off is testable locally.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { API_ORIGIN } from "../config";
import { AuthContext, type AppAuth } from "./context";
import { probeDoctorAuthz } from "./probe";

export function DevAuthProvider({ children }: { children: ReactNode }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [authz, setAuthz] = useState<AppAuth["authz"]>("checking");
  const [role, setRole] = useState<string | null>(null);

  // The dev path authorizes through the same /v1/doctor/me probe as Cognito (no bearer — the session
  // cookie rides on credentials). A 200 means a valid dev session AND server authorization.
  const probe = useCallback(async () => {
    const result = await probeDoctorAuthz();
    setIsSignedIn(result.authz === "authorized");
    setAuthz(result.authz);
    setRole(result.role);
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    void probe();
  }, [probe]);

  const value = useMemo<AppAuth>(
    () => ({
      isLoaded,
      isSignedIn,
      authz,
      role,
      displayName: isSignedIn ? "Provider (dev)" : null,
      getToken: async () => null,
      signOut: () => {
        setIsSignedIn(false);
        setAuthz("denied");
      },
      devSignIn: async () => {
        await fetch(`${API_ORIGIN}/dev-login?as=doctor`, { credentials: "include" });
        await probe();
      },
    }),
    [isLoaded, isSignedIn, authz, role, probe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
